<!-- Public route: /knowledge-exam-mark — no auth required (see router.js publicRoutes). -->
<template>
    <div class="wisdom-exam-page">
        <div class="hero-banner">
            <div class="hero-shape hero-shape-1"></div>
            <div class="hero-shape hero-shape-2"></div>
            <div class="hero-logos">
                <div class="hero-logo-chip">
                    <img src="https://guideportal.wisdomislam.org/uploads/image/1785763283.png" alt="E-Madrasa Junior">
                </div>
                <div class="hero-logo-chip">
                    <img src="https://guideportal.wisdomislam.org/uploads/image/1785763213.png" alt="E-Madrasa Senior">
                </div>
            </div>
            <h1 class="hero-title">Wisdom Students Knowledge Exam</h1>
            <p class="subtitle mal-text">വിസ്ഡം സ്റ്റുഡന്റ്സ് നോളജ് എക്സാം</p>
            <span class="status-badge mal-text">വിജയികളെ ചേർക്കുക (Winners Entry)</span>
        </div>

        <div class="page-wrapper">
            <div class="floating-card">
                <div class="alert-box" :class="alertType === 'success' ? 'alert-success' : 'alert-error'" v-if="alertMessage">
                    {{ alertMessage }}
                </div>

                <!-- STEP: CENTER -->
                <div class="step-container" :class="{ active: currentStep === 'center' }">
                    <h2 class="section-title mal-text">പരീക്ഷാ കേന്ദ്രം (Exam Center)</h2>
                    <div class="form-group">
                        <label class="form-label mal-text">ജില്ല (District) <span class="req">*</span></label>
                        <select v-model="centerSearch.district_id" class="form-input" @change="onCenterDistrictChange">
                            <option value="" disabled>Select District</option>
                            <option v-for="d in districts" :key="d.id" :value="d.id">{{ d.name }}</option>
                        </select>
                    </div>
                    <div class="form-group">
                        <label class="form-label mal-text">മണ്ഡലം (Zone) <span class="req">*</span></label>
                        <select v-model="centerSearch.zone_id" class="form-input" :disabled="!centerSearch.district_id" @change="onCenterZoneChange">
                            <option value="" disabled>Select Zone</option>
                            <option v-for="z in centerZones" :key="z.id" :value="z.id">{{ z.name }}</option>
                        </select>
                    </div>

                    <template v-if="!addingNewCenter">
                        <div class="form-group" v-if="centerSearch.zone_id">
                            <label class="form-label mal-text">കേന്ദ്രം തിരഞ്ഞെടുക്കുക (Select Center)</label>
                            <select v-if="zoneCenters.length" v-model="selectedCenterId" class="form-input">
                                <option value="" disabled>Select Center</option>
                                <option v-for="c in zoneCenters" :key="c.id" :value="c.id">{{ c.name }}<template v-if="c.address"> — {{ c.address }}</template></option>
                            </select>
                            <div class="center-fallback mal-text" v-else-if="zoneCentersChecked">
                                ഈ മണ്ഡലത്തിൽ കേന്ദ്രങ്ങളൊന്നും കണ്ടെത്തിയില്ല. താഴെ പുതിയ കേന്ദ്രം ചേർക്കുക.
                            </div>
                        </div>
                        <button type="button" class="btn-outline mal-text" v-if="centerSearch.zone_id" @click="addingNewCenter = true">
                            + പുതിയ കേന്ദ്രം ചേർക്കുക (Add New Center)
                        </button>
                        <button type="button" class="btn-main mal-text" :disabled="!selectedCenterId" @click="confirmExistingCenter">
                            തുടരുക (Continue)
                        </button>
                    </template>

                    <template v-else>
                        <div class="form-group">
                            <label class="form-label mal-text">കേന്ദ്രത്തിന്റെ പേര് (Center Name) <span class="req">*</span></label>
                            <input type="text" v-model="newCenter.name" class="form-input" placeholder="Center name">
                        </div>
                        <div class="form-group">
                            <label class="form-label mal-text">വിലാസം (Address)</label>
                            <input type="text" v-model="newCenter.address" class="form-input" placeholder="Address">
                        </div>
                        <div class="form-group">
                            <label class="form-label mal-text">കോർഡിനേറ്റർ/ഇൻവിജിലേറ്റർ പേര് (Coordinator/Invigilator Name) <span class="req">*</span></label>
                            <input type="text" v-model="newCenter.coordinator_name" class="form-input" placeholder="Name">
                        </div>
                        <div class="form-group">
                            <label class="form-label mal-text">മൊബൈൽ നമ്പർ (Mobile Number) <span class="req">*</span></label>
                            <input type="tel" v-model="newCenter.coordinator_mobile" class="form-input" placeholder="10 digit number" pattern="[0-9]{10}" maxlength="10" inputmode="numeric">
                        </div>
                        <p class="error-feedback" v-if="centerFormError">{{ centerFormError }}</p>
                        <button type="button" class="btn-main mal-text" :disabled="savingCenter" @click="submitNewCenter">
                            <span v-if="!savingCenter">കേന്ദ്രം ചേർക്കുക & തുടരുക (Add & Continue)</span>
                            <div class="loading-spinner" v-else></div>
                        </button>
                        <button type="button" class="link-btn mal-text" @click="addingNewCenter = false">ബാക്ക് (Back)</button>
                    </template>
                </div>

                <!-- STEP: CATEGORY -->
                <div class="step-container" :class="{ active: currentStep === 'category' }">
                    <h2 class="section-title mal-text">{{ centerName }}</h2>
                    <p class="mal-text" style="text-align:center;color:var(--text-light);margin-bottom:18px;">വിഭാഗം തിരഞ്ഞെടുക്കുക (Select Category)</p>
                    <button type="button" class="btn-main mal-text" :disabled="submittedCategories.includes('junior')" @click="goToWinnersForm('junior')">
                        Junior <span v-if="submittedCategories.includes('junior')">(സമർപ്പിച്ചു)</span>
                    </button>
                    <button type="button" class="btn-outline mal-text" :disabled="submittedCategories.includes('senior')" @click="goToWinnersForm('senior')">
                        Senior <span v-if="submittedCategories.includes('senior')">(സമർപ്പിച്ചു)</span>
                    </button>
                    <button type="button" class="link-btn mal-text" @click="currentStep = 'center'; selectedCenterId = ''; submittedCategories = [];">
                        മറ്റൊരു കേന്ദ്രം (Different Center)
                    </button>
                </div>

                <!-- STEP: WINNERS FORM -->
                <div class="step-container" :class="{ active: currentStep === 'winners' }">
                    <h2 class="section-title mal-text">{{ category === 'junior' ? 'Junior' : 'Senior' }} — വിജയികൾ (Winners)</h2>
                    <form @submit.prevent="submitWinners">
                        <div class="form-group">
                            <label class="form-label mal-text">കോർഡിനേറ്റർ/ഇൻവിജിലേറ്റർ പേര് (Coordinator/Invigilator Name) <span class="req">*</span></label>
                            <input type="text" v-model="winnersForm.coordinator_name" class="form-input" placeholder="Name" required>
                        </div>
                        <div class="form-group">
                            <label class="form-label mal-text">മൊബൈൽ നമ്പർ (Mobile Number) <span class="req">*</span></label>
                            <input type="tel" v-model="winnersForm.coordinator_mobile" class="form-input" placeholder="10 digit number" pattern="[0-9]{10}" maxlength="10" inputmode="numeric" required>
                        </div>
                        <div class="form-group">
                            <label class="form-label mal-text">പരീക്ഷ എഴുതിയ വിദ്യാർത്ഥികളുടെ എണ്ണം (No. of Students Attended) <span class="req">*</span></label>
                            <input type="number" min="1" v-model.number="winnersForm.students_attended" class="form-input" placeholder="Number of students" required @input="regenerateWinnerBlocks">
                        </div>

                        <div class="center-fallback mal-text" v-if="winnersForm.students_attended > 0" style="margin-bottom:18px;">
                            ഈ എണ്ണം അനുസരിച്ച് പരമാവധി {{ maxWinners }} വിജയികളെ ചേർക്കാം.
                            (Based on this count, up to {{ maxWinners }} winners can be added.)
                        </div>

                        <div class="winner-block" v-for="block in winnerBlocks" :key="block.position">
                            <button type="button" class="winner-block-header" @click="toggleWinnerBlock(block.position)">
                                <span class="winner-block-title mal-text">
                                    Winner {{ block.position }}
                                    <span class="winner-block-badge" v-if="winnerBlockFilledCount(block)">
                                        {{ winnerBlockFilledCount(block) }}/{{ block.entries.length }} filled
                                    </span>
                                </span>
                                <ion-icon name="chevron-down-outline" class="winner-block-chevron" :class="{ open: isWinnerBlockExpanded(block.position) }"></ion-icon>
                            </button>

                            <div class="winner-block-body" v-show="isWinnerBlockExpanded(block.position)">
                                <div class="winner-entry" v-for="(entry, eIndex) in block.entries" :key="eIndex">
                                    <div class="winner-entry-header" v-if="eIndex > 0">
                                        <span class="mal-text">Tied Winner {{ block.position }} — #{{ eIndex + 1 }}</span>
                                        <button type="button" class="tie-remove-btn" @click="removeTiedEntry(block, eIndex)">
                                            <ion-icon name="close-outline"></ion-icon>
                                        </button>
                                    </div>
                                    <div class="form-group">
                                        <label class="form-label mal-text">പേര് (Name) <span class="req">*</span></label>
                                        <input type="text" v-model="entry.name" class="form-input" placeholder="Full name">
                                    </div>
                                    <div class="form-group">
                                        <label class="form-label mal-text">രക്ഷിതാവിന്റെ പേര് (Parent Name)</label>
                                        <input type="text" v-model="entry.parent_name" class="form-input" placeholder="Parent's name">
                                    </div>
                                    <div class="form-group">
                                        <label class="form-label mal-text">മൊബൈൽ നമ്പർ (Mobile Number)</label>
                                        <input type="tel" v-model="entry.mobile" class="form-input" placeholder="10 digit number" pattern="[0-9]{10}" maxlength="10" inputmode="numeric">
                                    </div>
                                    <div class="form-group">
                                        <label class="form-label mal-text">ജനന തീയതി (DOB)</label>
                                        <input type="date" v-model="entry.dob" class="form-input">
                                    </div>
                                    <div class="form-group">
                                        <label class="form-label mal-text">സ്ഥലം (Place)</label>
                                        <input type="text" v-model="entry.place" class="form-input" placeholder="Place">
                                    </div>
                                    <div class="form-group">
                                        <label class="form-label mal-text">മാർക്ക് (Mark) <span class="req">*</span></label>
                                        <input type="number" step="0.5" min="0" v-model.number="entry.mark" class="form-input" placeholder="Mark">
                                    </div>
                                </div>
                                <button type="button" class="tie-add-btn mal-text" @click="addTiedEntry(block)">
                                    <ion-icon name="add-circle-outline"></ion-icon>
                                    ഈ റാങ്കിൽ ഒരു വിജയി കൂടി ചേർക്കുക (Add another winner tied at this rank)
                                </button>
                            </div>
                        </div>

                        <p class="error-feedback" v-if="winnersFormError">{{ winnersFormError }}</p>
                        <button type="submit" class="btn-main mal-text" :disabled="submittingWinners || !winnerBlocks.length">
                            <span v-if="!submittingWinners">സമർപ്പിക്കുക (Submit)</span>
                            <div class="loading-spinner" v-else></div>
                        </button>
                        <button type="button" class="link-btn mal-text" @click="currentStep = 'category'">ബാക്ക് (Back)</button>
                    </form>
                </div>

                <!-- STEP: SUCCESS -->
                <div class="step-container" :class="{ active: currentStep === 'success' }">
                    <div class="result-box">
                        <div class="success-icon">
                            <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#4a4fa0" stroke-width="2">
                                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                                <polyline points="22 4 12 14.01 9 11.01"></polyline>
                            </svg>
                        </div>
                        <h3 class="mal-text">വിജയികളുടെ വിവരങ്ങൾ സമർപ്പിച്ചു!</h3>
                        <p class="mal-text result-sub">Winners Submitted Successfully</p>
                        <button type="button" class="btn-main mal-text" @click="currentStep = 'category'">
                            മറ്റൊരു വിഭാഗം സമർപ്പിക്കുക (Submit Other Category)
                        </button>
                        <button type="button" class="link-btn mal-text" @click="currentStep = 'center'; selectedCenterId = '';">ഹോം (Home)</button>
                    </div>
                </div>
            </div>
        </div>
        <footer class="page-footer mal-text">© 2026 | IT Wing Wisdom Islamic Organization.</footer>
    </div>
</template>

<script>
export default {
    name: 'WisdomExamWinners',
    data() {
        return {
            API_BASE: 'https://guideportal.wisdomislam.org/api/public/wisdom-exam',
            LOCATION_API_BASE: 'https://guideportal.wisdomislam.org/api/location',
            currentStep: 'center',
            alertMessage: '',
            alertType: 'error',

            districts: [],
            centerZones: [],
            zoneCenters: [],
            zoneCentersChecked: false,

            centerSearch: { district_id: '', zone_id: '' },
            selectedCenterId: '',
            centerName: '',

            addingNewCenter: false,
            newCenter: { name: '', address: '', coordinator_name: '', coordinator_mobile: '' },
            centerFormError: '',
            savingCenter: false,

            category: '',
            submittedCategories: [],

            winnersForm: {
                coordinator_name: '',
                coordinator_mobile: '',
                students_attended: '',
            },
            winnerBlocks: [],
            // Collapsed by default — {position: true/false} — so a long winner list (up to 10
            // blocks) doesn't dump every field on screen at once on mobile.
            expandedPositions: {},
            winnersFormError: '',
            submittingWinners: false,
        };
    },
    computed: {
        maxWinners() {
            const n = Number(this.winnersForm.students_attended) || 0;
            if (n <= 0) return 0;
            if (n <= 10) return 3;
            if (n <= 20) return 5;
            if (n <= 30) return 7;
            return 10;
        },
    },
    mounted() {
        this.loadDistricts();
    },
    methods: {
        showAlert(message, type = 'error') {
            this.alertMessage = message;
            this.alertType = type;
            setTimeout(() => { this.alertMessage = ''; }, 4000);
        },
        blankEntry() {
            return { name: '', parent_name: '', mobile: '', dob: '', place: '', mark: '' };
        },
        async loadDistricts() {
            try {
                const response = await fetch(this.LOCATION_API_BASE + '/districts?state_id=1');
                const data = await response.json();
                const list = (data.data && data.data.districts) || [];
                this.districts = list.filter((d) => String(d.name || '').trim().toUpperCase() !== 'FOREIGN');
            } catch (e) {
                this.districts = [];
            }
        },
        onCenterDistrictChange() {
            this.centerSearch.zone_id = '';
            this.centerZones = [];
            this.zoneCenters = [];
            this.zoneCentersChecked = false;
            this.selectedCenterId = '';
            this.loadCenterZones();
        },
        async loadCenterZones() {
            if (!this.centerSearch.district_id) return;
            try {
                const response = await fetch(this.LOCATION_API_BASE + '/zones?district_id=' + this.centerSearch.district_id);
                const data = await response.json();
                this.centerZones = (data.data && data.data.zones) || [];
            } catch (e) {
                this.centerZones = [];
            }
        },
        onCenterZoneChange() {
            this.selectedCenterId = '';
            this.addingNewCenter = false;
            this.loadZoneCenters();
        },
        async loadZoneCenters() {
            if (!this.centerSearch.zone_id) return;
            this.zoneCentersChecked = false;
            try {
                const response = await fetch(this.API_BASE + '/exam-centers?zone_id=' + this.centerSearch.zone_id);
                const data = await response.json();
                this.zoneCenters = data.data || [];
            } catch (e) {
                this.zoneCenters = [];
            } finally {
                this.zoneCentersChecked = true;
            }
        },
        confirmExistingCenter() {
            const c = this.zoneCenters.find((x) => x.id === this.selectedCenterId);
            this.centerName = c ? c.name : '';
            this.submittedCategories = [];
            this.currentStep = 'category';
        },
        async submitNewCenter() {
            this.centerFormError = '';
            if (!this.newCenter.name || !this.newCenter.coordinator_name || !/^[0-9]{10}$/.test(this.newCenter.coordinator_mobile)) {
                this.centerFormError = 'എല്ലാ നിർബന്ധിത ഫീൽഡുകളും പൂരിപ്പിക്കുക.';
                return;
            }
            this.savingCenter = true;
            try {
                const response = await fetch(this.API_BASE + '/create-center', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                    body: JSON.stringify({
                        name: this.newCenter.name,
                        address: this.newCenter.address,
                        district_id: this.centerSearch.district_id,
                        zone_id: this.centerSearch.zone_id,
                        coordinator_name: this.newCenter.coordinator_name,
                        coordinator_mobile: this.newCenter.coordinator_mobile,
                    }),
                });
                const data = await response.json();
                if (data.status !== 'success') {
                    this.centerFormError = data.message || 'കേന്ദ്രം ചേർക്കാൻ കഴിഞ്ഞില്ല.';
                    return;
                }
                this.selectedCenterId = data.data.id;
                this.centerName = data.data.name;
                this.addingNewCenter = false;
                this.submittedCategories = [];
                this.currentStep = 'category';
            } catch (e) {
                this.centerFormError = 'എന്തോ തെറ്റ് സംഭവിച്ചു. ദയവായി വീണ്ടും ശ്രമിക്കുക.';
            } finally {
                this.savingCenter = false;
            }
        },
        goToWinnersForm(cat) {
            this.category = cat;
            this.winnersForm = {
                coordinator_name: this.newCenter.coordinator_name || '',
                coordinator_mobile: this.newCenter.coordinator_mobile || '',
                students_attended: '',
            };
            this.winnerBlocks = [];
            this.expandedPositions = {};
            this.winnersFormError = '';
            this.currentStep = 'winners';
        },
        // Rebuilds the winner blocks to match the current max-winners count, keeping any data
        // already typed in blocks/ranks that still exist.
        regenerateWinnerBlocks() {
            const max = this.maxWinners;
            if (!max) {
                this.winnerBlocks = [];
                return;
            }
            const blocks = [];
            for (let i = 1; i <= max; i++) {
                const existing = this.winnerBlocks.find((b) => b.position === i);
                blocks.push(existing || { position: i, entries: [this.blankEntry()] });
                // New blocks start collapsed; leaves an already-toggled block's state untouched.
                if (!(i in this.expandedPositions)) this.expandedPositions[i] = false;
            }
            this.winnerBlocks = blocks;
        },
        isWinnerBlockExpanded(position) {
            return !!this.expandedPositions[position];
        },
        toggleWinnerBlock(position) {
            this.expandedPositions[position] = !this.expandedPositions[position];
        },
        // Small "N/M filled" hint shown on the collapsed header — counts entries with at least a
        // name typed, so an admin can tell at a glance which ranks still need attention.
        winnerBlockFilledCount(block) {
            return block.entries.filter((e) => e.name && e.name.trim() !== '').length;
        },
        addTiedEntry(block) {
            block.entries.push(this.blankEntry());
        },
        removeTiedEntry(block, index) {
            block.entries.splice(index, 1);
        },
        async submitWinners() {
            this.winnersFormError = '';
            if (!/^[0-9]{10}$/.test(this.winnersForm.coordinator_mobile)) {
                this.winnersFormError = 'സാധുവായ 10 അക്ക മൊബൈൽ നമ്പർ നൽകുക.';
                return;
            }
            const winners = [];
            this.winnerBlocks.forEach((block) => {
                block.entries.forEach((entry) => {
                    if (!entry.name || entry.mark === '' || entry.mark === null) return;
                    winners.push({
                        position: block.position,
                        name: entry.name,
                        parent_name: entry.parent_name,
                        mobile: entry.mobile,
                        dob: entry.dob,
                        place: entry.place,
                        mark: entry.mark,
                    });
                });
            });
            if (!winners.length) {
                this.winnersFormError = 'ചുരുങ്ങിയത് ഒരു വിജയിയെയെങ്കിലും ചേർക്കുക.';
                return;
            }

            this.submittingWinners = true;
            try {
                const response = await fetch(this.API_BASE + '/submit-winners', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                    body: JSON.stringify({
                        exam_center_id: this.selectedCenterId,
                        category: this.category,
                        students_attended: this.winnersForm.students_attended,
                        coordinator_name: this.winnersForm.coordinator_name,
                        coordinator_mobile: this.winnersForm.coordinator_mobile,
                        winners,
                    }),
                });
                const data = await response.json();
                if (data.status !== 'success') {
                    this.winnersFormError = data.message || 'സമർപ്പിക്കാൻ കഴിഞ്ഞില്ല.';
                    return;
                }
                this.submittedCategories.push(this.category);
                this.currentStep = 'success';
            } catch (e) {
                this.winnersFormError = 'എന്തോ തെറ്റ് സംഭവിച്ചു. ദയവായി വീണ്ടും ശ്രമിക്കുക.';
            } finally {
                this.submittingWinners = false;
            }
        },
    },
};
</script>

<style>
#appCapsule.wisdom-exam-shell {
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    margin-top: 0 !important;
    margin-bottom: 0 !important;
    background: #f6f4fb !important;
}
body:has(#appCapsule.wisdom-exam-shell) {
    background: #f6f4fb !important;
}
</style>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&family=Baloo+2:wght@700;800&display=swap');

.wisdom-exam-page {
    --primary: #4a4fa0;
    --primary-dark: #34386f;
    --primary-light: #7478c9;
    --accent: #2e9e55;
    --accent-light: #e3f5e9;
    --bg-color: #f6f4fb;
    --input-bg: #f7f5fc;
    --text-main: #241a35;
    --text-light: #7c7290;

    font-family: 'Outfit', sans-serif;
    background: var(--bg-color);
    color: var(--text-main);
    line-height: 1.5;
    min-height: 100vh;
    padding: 0;
    margin: 0;
}

.hero-banner {
    position: relative;
    overflow: hidden;
    background: #ffffff;
    padding: 36px 20px 76px;
    text-align: center;
}

.hero-shape {
    position: absolute;
    border-radius: 50%;
    background: rgba(74, 79, 160, 0.07);
}
.hero-shape-1 { width: 220px; height: 220px; top: -90px; right: -60px; }
.hero-shape-2 { width: 140px; height: 140px; bottom: -60px; left: -40px; background: rgba(46, 158, 85, 0.1); }

.hero-logos {
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 14px;
    margin-bottom: 20px;
}

.hero-logo-chip {
    width: min(42vw, 165px);
    aspect-ratio: 797 / 453;
    overflow: hidden;
    position: relative;
    animation: logoPop 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) backwards;
}
.hero-logo-chip:nth-child(2) { animation-delay: 0.12s; }

.hero-logo-chip img {
    position: absolute;
    width: 196%;
    height: 345%;
    left: -49%;
    top: -93%;
    max-width: none;
    filter: drop-shadow(0 3px 8px rgba(0, 0, 0, 0.3));
    transition: transform 0.25s ease;
}
.hero-logo-chip img:hover {
    transform: scale(1.05);
}

@keyframes logoPop {
    from { opacity: 0; transform: scale(0.75) translateY(8px); }
    to { opacity: 1; transform: scale(1) translateY(0); }
}

.hero-title {
    position: relative;
    font-family: 'Baloo 2', 'Outfit', sans-serif;
    font-size: 26px;
    font-weight: 800;
    letter-spacing: 0.2px;
    line-height: 1.25;
    margin-bottom: 8px;
    background: linear-gradient(120deg, var(--primary) 0%, var(--primary-light) 55%, var(--accent) 100%);
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    color: var(--primary);
}

.hero-banner .subtitle {
    position: relative;
    font-size: 14px;
    color: var(--text-light);
    font-weight: 400;
}

.status-badge {
    position: relative;
    display: inline-block;
    background: var(--accent);
    color: #fff;
    padding: 5px 16px;
    border-radius: 100px;
    font-size: 12.5px;
    font-weight: 700;
    margin-top: 14px;
    box-shadow: 0 4px 10px rgba(46, 158, 85, 0.4);
}

.page-wrapper {
    width: 100%;
    max-width: 500px;
    margin: -46px auto 0;
    padding: 0 18px 60px;
    position: relative;
}

.floating-card {
    background: #fff;
    border-radius: 22px;
    padding: 26px 22px 30px;
    box-shadow: 0 16px 40px rgba(74, 79, 160, 0.16);
}

.section-title {
    font-size: 16px;
    font-weight: 700;
    color: var(--primary);
    margin-bottom: 18px;
    text-align: center;
}

.alert-box {
    padding: 14px 16px;
    border-radius: 12px;
    font-size: 13.5px;
    margin-bottom: 16px;
    text-align: center;
}
.alert-error { background: #fdf0f0; color: #b91c1c; }
.alert-success { background: #f1ebfc; color: var(--primary); }

.form-group {
    margin-bottom: 18px;
}

.form-label {
    display: block;
    margin-bottom: 7px;
    font-size: 13px;
    font-weight: 600;
    color: var(--primary);
    margin-left: 2px;
}
.req { color: #dc2626; }

.form-input {
    width: 100%;
    padding: 14px 16px;
    background: var(--input-bg);
    border: 1.5px solid #ece7f7;
    border-radius: 12px;
    font-size: 15.5px;
    font-family: inherit;
    color: var(--text-main);
    transition: all 0.2s ease;
}
.form-input:focus {
    outline: none;
    background: #fff;
    border-color: var(--primary-light);
    box-shadow: 0 0 0 4px rgba(116, 120, 201, 0.15);
}
.form-input:disabled {
    opacity: 0.55;
}

.btn-main {
    width: 100%;
    padding: 16px;
    background: linear-gradient(135deg, var(--primary), var(--primary-light));
    color: #fff;
    border: none;
    border-radius: 14px;
    font-size: 15px;
    font-weight: 700;
    letter-spacing: 0.3px;
    cursor: pointer;
    transition: transform 0.15s, box-shadow 0.15s;
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: 6px;
    box-shadow: 0 8px 18px rgba(74, 79, 160, 0.28);
}
.btn-main:active { transform: scale(0.98); }
.btn-main:disabled { background: #b7aecb; box-shadow: none; cursor: not-allowed; }

.btn-outline {
    width: 100%;
    padding: 16px;
    background: #fff;
    color: var(--primary);
    border: 2px solid var(--primary);
    border-radius: 14px;
    font-size: 15px;
    font-weight: 700;
    cursor: pointer;
    margin-top: 14px;
    transition: transform 0.15s;
}
.btn-outline:active { transform: scale(0.98); }
.btn-outline:disabled { opacity: 0.5; cursor: not-allowed; }

.link-btn {
    display: block;
    width: 100%;
    text-align: center;
    background: none;
    border: none;
    color: var(--text-light);
    font-size: 13.5px;
    padding: 14px 0 0;
    cursor: pointer;
}

.loading-spinner {
    width: 20px;
    height: 20px;
    border: 3px solid rgba(255, 255, 255, 0.4);
    border-top-color: #fff;
    border-radius: 50%;
    animation: spin 0.7s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

.step-container {
    display: none;
    animation: fadeIn 0.4s forwards;
}
.step-container.active { display: block; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }

.center-fallback {
    background: var(--accent-light);
    color: #1f6b3a;
    border-radius: 12px;
    padding: 14px 16px;
    font-size: 13px;
    line-height: 1.5;
    border: 1px solid rgba(46, 158, 85, 0.35);
}

.page-footer {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    text-align: center;
    font-size: 12px;
    color: var(--text-light);
    padding: 10px 20px;
    background: rgba(255, 255, 255, 0.9);
    backdrop-filter: blur(4px);
    z-index: 20;
}

.result-box { text-align: center; }
.success-icon {
    width: 58px;
    height: 58px;
    border-radius: 50%;
    background: #eceef8;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 16px;
}
.result-box h3 {
    color: var(--primary);
    font-size: 18.5px;
    margin-bottom: 4px;
}
.result-sub {
    color: var(--text-light);
    font-size: 13px;
    margin-bottom: 20px;
}

.error-feedback {
    color: #dc2626;
    font-size: 13px;
    text-align: center;
    margin-bottom: 10px;
}

/* Winner blocks — one per rank/position, each holding one or more tied entries. Collapsed by
   default (header-only) so a long winner list doesn't dump every field on screen at once. */
.winner-block {
    border: 1.5px solid #ece7f7;
    border-radius: 16px;
    margin-bottom: 14px;
    background: var(--input-bg);
    overflow: hidden;
}
.winner-block-header {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 15px 16px;
    background: none;
    border: none;
    cursor: pointer;
    text-align: left;
}
.winner-block-title {
    font-size: 15px;
    font-weight: 700;
    color: var(--primary);
    display: flex;
    align-items: center;
    gap: 8px;
}
.winner-block-badge {
    font-size: 11px;
    font-weight: 600;
    color: var(--accent);
    background: var(--accent-light);
    padding: 3px 9px;
    border-radius: 100px;
}
.winner-block-chevron {
    font-size: 18px;
    color: var(--primary-light);
    transition: transform 0.2s ease;
    flex-shrink: 0;
}
.winner-block-chevron.open {
    transform: rotate(180deg);
}
.winner-block-body {
    padding: 0 16px 16px;
}
.winner-entry {
    background: #fff;
    border: 1px solid #ece7f7;
    border-radius: 12px;
    padding: 14px 14px 2px;
    margin-bottom: 12px;
}
.winner-entry-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
    font-size: 12.5px;
    font-weight: 600;
    color: var(--text-light);
}
.tie-remove-btn {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    border: none;
    background: #fdf0f0;
    color: #dc2626;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    cursor: pointer;
}
/* An actual button (not plain text) so it reads as clickable, matching the rest of the design
   system's buttons but sized down and dashed to signal "add another" rather than "submit". */
.tie-add-btn {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 11px 14px;
    background: #fff;
    border: 1.5px dashed var(--primary-light);
    border-radius: 12px;
    color: var(--primary);
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.15s;
}
.tie-add-btn:active {
    background: var(--input-bg);
}
.tie-add-btn ion-icon {
    font-size: 17px;
    flex-shrink: 0;
}
</style>
